<script lang="ts">
  /* THE SCALE BAR, LAID OUT RATHER THAN POSITIONED.
   *
   * MapLibre puts its own in `.maplibregl-ctrl-bottom-left`, anchored to the
   * map container's corner — which is where the credit line is. Two elements
   * anchored independently to the same corner is how this app's whole history
   * of collisions starts, and the previous fix for it was
   *
   *     bottom: calc(var(--gd-controls-bottom) + 100px) !important;
   *
   * where 100 was "the scrubber and the tool row, together": a constant in a
   * stylesheet describing the rendered height of two elements in other files,
   * and the only one that had to shout to win.
   *
   * The app mounts the single instance HERE instead, as a flex child, so the
   * measurement is laid out wherever the host puts it.
   */
  /* ═══ TYPE-ONLY AT THE TOP, THE CLASS AT USE ═══
   *
   * `import { ScaleControl } from 'maplibre-gl'` is a STATIC import of the
   * whole library, and `<Chart>` goes to some trouble to load MapLibre lazily —
   * it is ~1 MB and the chart is not on the critical path. Rollup said so:
   * "dynamically imported by chart-ui/src/Chart.svelte but also statically
   * imported by src/Scale.svelte, dynamic import will not move module into
   * another chunk". One line in a scale bar undid the app's whole load
   * strategy.
   *
   * The type import is erased at compile time and costs nothing. */
  import type { Map as MlMap } from 'maplibre-gl';

  let { map = null }: { map?: MlMap | null } = $props();

  let host = $state<HTMLElement | null>(null);
  $effect(() => {
    const m = map;
    const el = host;
    if (!m || !el) return;
    /* By the time there is a map instance, MapLibre is loaded — so this import
       resolves from cache and adds no request. */
    let cleanup: (() => void) | null = null;
    let cancelled = false;
    void import('maplibre-gl').then(({ ScaleControl }) => {
      if (cancelled) return;
      const ctrl = new ScaleControl({ unit: 'nautical' });
      const node = ctrl.onAdd(m);
      el.appendChild(node);
      cleanup = () => {
        ctrl.onRemove();
        node.remove();
      };
    });
    return () => {
      cancelled = true;
      cleanup?.();
    };
  });
</script>

<span class="scale" bind:this={host} title="Scale bar — distances in nautical miles"></span>

<style>
  .scale {
    display: inline-block;
  }
  /* MapLibre's own element, inside our box: it draws a bordered white bar and
     we only want the measurement. `:global` because MapLibre owns these class
     names and Svelte will not scope a selector it never saw in the markup. */
  .scale :global(.maplibregl-ctrl-scale) {
    position: static;
    margin: 0;
    background: none;
    border: 0;
    border-bottom: 2px solid var(--sub);
    border-left: 2px solid var(--sub);
    border-right: 2px solid var(--sub);
    color: var(--sub);
    font: var(--fs-micro) / 1.6 var(--mono);
    padding: 0 var(--sp-2);
    text-shadow: 0 1px 2px var(--ovl);
  }
</style>
